import Control from "../../../assets/vfx/controls/control";
import {
  IControlStyles,
  PageContent,
  useControlStyles,
} from "../../../assets/layouts";
import React from "react";
import Form from "../../../assets/vfx/form/form";
import { Col } from "react-bootstrap";
import { SearchContainer } from "../../../assets/vfx/search/searchContainer";
import {
  SortDirection,
  SortModel,
} from "../../../assets/utils/lib/searchDto";
import { CountyService } from "./countyService";
import { Constants } from "../../../assets/utils/lib/constants";
import { GRID_ACTION_TYPES } from "../../../assets/types";
import { ControlDataGridColumnProps } from "../../../assets/vfx/controls/components/datagrid/controlDataGrid";
import { SearchCountyDto } from "../../../services/dto";
import { DropdownOption } from "../../../assets/vfx/controls/components/dropdown/controlDropdown";
import {
  createActionColumn,
  createLayoutOptions,
} from "../../../assets/configuration/gridConfig";

interface CountyPageProps {
  controlStyles?: IControlStyles | undefined;
  onCustomEvent?: (actionType: string, dataId: string) => void;
  stateLookup?: DropdownOption[] | undefined;
}

interface CountySearch {
  name: string;
  stateId: string;
}

const searchInitialValues: CountySearch = {
  name: Constants.emptyString,
  stateId: Constants.emptyGuid,
};

const sortBy: SortModel = {
  field: "countyName",
  direction: SortDirection.Ascending,
};

const defaultSearchQuery: SearchCountyDto = {
  name: "",
  pageSize: 10,
  pageNumber: 1,
  sortBy: sortBy,
  stateId: undefined,
  regionId: undefined,
};

const DATA_ID_FIELD = "countyId";

const createGridColumns = (): ControlDataGridColumnProps[] => [
  {
    data: "countyID",
    title: "ID",
    visible: false,
    columnType: "number",
    alignment: "right",
  },
  {
    data: "countyName",
    title: "Name",
    visible: true,
    columnType: "text",
    alignment: "left",
    sortable: true,
    width: "36%",
  },
  {
    data: "countyDescription",
    title: "Description",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "36%",
  },
];

export const CountyPage = (props: CountyPageProps) => {
  const [searchQuery, setSearchQuery] =
    React.useState<SearchCountyDto>(defaultSearchQuery);
  const countyGridQuery = CountyService.gridQuery({
    ...searchQuery,
    stateId:
      searchQuery.stateId && searchQuery.stateId !== Constants.emptyGuid
        ? searchQuery.stateId
        : undefined,
  });
  const { data } = countyGridQuery;
  const isGridLoading = countyGridQuery.isLoading || countyGridQuery.isFetching;

  const styles: IControlStyles = props?.controlStyles ?? useControlStyles();

  const counties = data?.result ?? [];
  const hasData = counties.length > 0;
  const gridColumns = createGridColumns();
  const actionColumn = createActionColumn(styles, DATA_ID_FIELD, [
    GRID_ACTION_TYPES.EDIT,
    GRID_ACTION_TYPES.VIEW,
  ]);
  const layoutOptions = createLayoutOptions(hasData);

  const handleSearch = (values: any) => {
    setSearchQuery({
      name: values.name,
      stateId: values.stateId,
      regionId: undefined,
      pageSize: 10,
      pageNumber: 1,
      sortBy: sortBy,
    });
  };

  const handleClear = (): void => {
      setSearchQuery(prev => ({
          ...defaultSearchQuery,
          sortBy: prev.sortBy,
          pageSize: prev.pageSize ?? 10,
      }));
  };

  const handleNew = () =>
    props?.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);

  return (
    <PageContent title="Counties">
      <SearchContainer
        initialValues={searchInitialValues}
        onSearch={handleSearch}
        onNew={handleNew}
        onClear={handleClear}
        enableReinitialize
      >
        <Col lg={4}>
          <Form.Text name={"name"} label={"Name"} />
        </Col>
        <Col lg={4} className="ms-2">
          <Form.Dropdown
            name={"stateId"}
            label={"State"}
            blankOption={{
              key: Constants.emptyGuid,
              display: "--Select State--",
              value: Constants.emptyGuid,
            }}
            options={props?.stateLookup}
          />
        </Col>
      </SearchContainer>
      <Control.DataGrid
        dataId="countyID"
        cols={gridColumns}
        data={counties}
        actionCol={actionColumn}
        layoutOptions={layoutOptions}
        onCustomEvent={props?.onCustomEvent}
        totalRecords={data?.totalSize ?? (data?.result?.length ?? 0)}
        query={searchQuery}
        loading={isGridLoading}
        onQueryChange={(patch) => setSearchQuery(query => ({ ...query, ...patch }))}
      />
    </PageContent>
  );
};
